import { createBrowserMicroAgent, MicroAgentInstance } from '@vp/newrelic-browser-micro-agent'
import config from 'config'
import { IS_BROWSER } from '~/app/constants'

/* eslint-disable @typescript-eslint/no-explicit-any */
//TODO MIGRATION T011: Fix eslint errors

export type PageMetadata = {
  [key: string]: string | number | undefined
  locale: string
  entityCount?: number
  galleryName?: string
  galleryId?: number
  productKey?: string
  quickViewId?: string
}

class BrowserInstrumentation {
  agent: MicroAgentInstance | null = null

  initNewRelicAgent(applicationID: string, licenseKey: string): void {
    const options = {
      info: {
        beacon: 'bam.nr-data.net',
        errorBeacon: 'bam.nr-data.net',
        licenseKey,
        applicationID,
        sa: 1,
      },
      loader_config: {
        accountID: config.client.newRelic.accountId,
        trustKey: config.client.newRelic.trustKey,
        agentID: applicationID, // For browser agent agentId usually the same as applicationId
        licenseKey,
        applicationID,
      },
      init: {
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: { deny_list: ['bam.nr-data.net'] },
      },
    }

    this.agent = createBrowserMicroAgent(options)
  }

  recordPageAction(name: string, payload: Record<string, any>): void {
    try {
      if (this.agent && this.agent.addPageAction) {
        this.agent.addPageAction(name, {
          ...payload,
        })
      }
    } catch {
      // best effort - don't break the page
    }
  }

  /**
   * Creates a `PageAction` event in Newrelic for the `designSelection`
   * action the user performed
   *
   * @param payload
   * @param selectionType
   */
  recordDesignSelection(payload: Record<string, any>, selectionType: string): void {
    this.recordPageAction('design-selection', {
      selectionType,
      ...payload,
    })
  }

  /**
   * Creates a `PageAction` event in Newrelic for the `pageReady` action
   * with the time it took until the user is first able to interact with the
   * app
   */
  recordAppHydratedEvent(): void {
    if (!this.agent) {
      return
    }

    if (IS_BROWSER) {
      const timing = (Date.now() - window.performance.timing.navigationStart) / 1000.0

      this.recordPageAction('application-hydrated', {
        timing,
      })
    }
  }

  /**
   * Sets the provided values as default attributes/metadata to the
   * instrumentation to be recorded on all actions/events
   *
   * @param params
   */
  setPageView(page: string, params: PageMetadata): void {
    if (!this.agent) {
      return
    }

    for (const [key, value] of Object.entries(params)) {
      if (value) {
        this.agent.setCustomAttribute(key, value)
      }
    }

    // Keeping culture for backwards compatibility
    this.agent.setCustomAttribute('culture', params.locale)

    this.agent.setPageViewName(page)
  }

  recordImageFailure(payload: Record<string, any>): void {
    this.recordPageAction('image-failure', {
      ...payload,
    })
  }

  recordImagesLoaded(): void {
    const imageLoadedTimes = window.imageResources.map((entry) => entry.responseEnd - entry.startTime)

    if (imageLoadedTimes.length > 3) {
      window.imageResources = []
      const minImageLoadedTime = Math.min(...imageLoadedTimes)
      const maxImageLoadedTime = Math.max(...imageLoadedTimes)
      const meanImageLoadedTime = imageLoadedTimes.reduce((sum, time) => sum + time, 0) / imageLoadedTimes.length

      this.recordPageAction('images-loaded', {
        imageLoadedCount: imageLoadedTimes.length,
        minImageLoadedTime,
        maxImageLoadedTime,
        meanImageLoadedTime,
      })
    }
  }
}

export const instrumenter = new BrowserInstrumentation()
